import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AlertTriangle, RefreshCw, Flame, MessageSquare, Mail, MessageCircle, ExternalLink, TrendingUp } from "lucide-react";

function normalizePhoneBR(phone) {
  if (!phone) return null;
  const digits = String(phone).replace(/\D/g, "");
  if (digits.length < 10) return null;
  return digits.startsWith("55") ? digits : `55${digits}`;
}

function buildWaReplyLink(lead) {
  const phone = normalizePhoneBR(lead.contact_phone);
  if (!phone) return null;
  const first = (lead.contact_name || "").split(" ")[0] || "";
  const parts = [
    `Olá${first ? " " + first : ""}, aqui é da RAF Direct!`,
    "Vi sua triagem com a nossa LIA e vim conversar com você.",
  ];
  if (lead.intent) parts.push(`Sobre ${String(lead.intent).toLowerCase()}: como posso te ajudar agora?`);
  if (lead.city_state) parts.push(`Vi que você está em ${lead.city_state}.`);
  const msg = parts.join(" ");
  return `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
}

export default function FallbackHeatmapWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(7);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/messaging/admin/fallback-metrics", { params: { days, environment: "production" } });
      setData(r.data);
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="rounded-2xl border border-slate-200 bg-white p-4 text-xs text-slate-400">Carregando fallbacks...</div>;

  const max = Math.max(1, ...data.heatmap.flat());
  const hot = data.hottest_slot || {};
  const wdLabels = data.weekday_labels || ["Seg","Ter","Qua","Qui","Sex","Sáb","Dom"];
  const topSlots = data.top_slots || [];

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="fallback-heatmap-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 font-bold text-[#0B2B62]">
          <AlertTriangle className="w-4 h-4 text-amber-600"/> Fallbacks sem plantão (últimos {data.period_days}d)
        </div>
        <div className="flex items-center gap-2">
          <select value={days} onChange={e=>setDays(Number(e.target.value))} className="border rounded-lg px-2 py-1 text-xs" data-testid="fallback-days-select">
            <option value={7}>7 dias</option>
            <option value={14}>14 dias</option>
            <option value={30}>30 dias</option>
          </select>
          <button onClick={load} disabled={loading} className="p-1 hover:bg-slate-100 rounded" data-testid="fallback-refresh"><RefreshCw className={`w-3.5 h-3.5 ${loading?"animate-spin":""}`}/></button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-center">
          <div className="text-2xl font-bold text-amber-900" data-testid="fallback-total">{data.total}</div>
          <div className="text-[10px] text-amber-800 uppercase tracking-wide">Leads sem plantão</div>
        </div>
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-2 text-center">
          <div className="text-2xl font-bold text-emerald-900 flex items-center justify-center gap-1"><Mail className="w-4 h-4"/>{data.email_sent}</div>
          <div className="text-[10px] text-emerald-800 uppercase tracking-wide">E-mail enviado</div>
        </div>
        <div className="rounded-lg bg-violet-50 border border-violet-200 p-2 text-center">
          <div className="text-2xl font-bold text-violet-900 flex items-center justify-center gap-1"><MessageSquare className="w-4 h-4"/>{data.sms_sent}</div>
          <div className="text-[10px] text-violet-800 uppercase tracking-wide">SMS enviado</div>
        </div>
      </div>

      {topSlots.length > 0 && (
        <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 p-2">
          <div className="text-[10px] text-amber-900 uppercase tracking-wide flex items-center gap-1 font-bold mb-1"><TrendingUp className="w-3 h-3"/> Horários críticos pra escalonar plantão</div>
          <div className="flex flex-wrap gap-1">
            {topSlots.map((s, i) => (
              <span key={i} className={`px-2 py-0.5 rounded text-[11px] font-bold ${i===0?"bg-amber-200 text-amber-900":"bg-amber-100 text-amber-800"}`} data-testid={`top-slot-${i}`}>
                {i===0 && <Flame className="inline w-3 h-3 mr-0.5"/>}
                {wdLabels[s.weekday]} {String(s.hour).padStart(2,"0")}h · {s.count}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="text-[10px] text-slate-500 mb-1">Heatmap por dia da semana × hora (horário de Brasília)</div>
      <div className="overflow-x-auto">
        <table className="text-[9px] w-full" data-testid="fallback-heatmap-table">
          <thead>
            <tr><th className="w-8"></th>{Array.from({length:24},(_,h)=><th key={h} className="w-4 text-slate-400 font-normal">{h%3===0?h:""}</th>)}</tr>
          </thead>
          <tbody>
            {data.heatmap.map((row, wd) => (
              <tr key={wd}>
                <td className="text-slate-500 pr-1 font-medium">{wdLabels[wd]}</td>
                {row.map((count, h) => {
                  const intensity = count === 0 ? 0 : Math.min(1, count / max);
                  const bg = count === 0 ? "#f8fafc" : `rgba(217, 119, 6, ${0.15 + intensity * 0.7})`;
                  return <td key={h} title={`${wdLabels[wd]} ${h}h: ${count}`} className="w-4 h-4 border border-white" style={{ backgroundColor: bg }} data-testid={`fallback-cell-${wd}-${h}`}>{count > 0 ? <span className="text-[8px] text-slate-800">{count}</span> : ""}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.latest && data.latest.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] text-slate-500 mb-2 uppercase tracking-wide">Últimos {data.latest.length} fallbacks · Toque no botão pra responder no WhatsApp</div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {data.latest.map(l => {
              const waLink = buildWaReplyLink(l);
              return (
                <div key={l.id} className="text-xs border border-slate-100 rounded-lg p-2 hover:border-slate-300 transition" data-testid={`fallback-latest-${l.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-[#0B2B62] truncate">{l.contact_name || "—"}</div>
                      <div className="text-slate-500 text-[11px]">{l.contact_phone || "sem telefone"} · {l.city_state || "sem cidade"}</div>
                      {l.current_issue && <div className="text-slate-600 text-[11px] mt-0.5 line-clamp-2">{l.current_issue}</div>}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <div className="text-[10px] text-slate-400">{new Date(l.created_at).toLocaleString("pt-BR",{day:"2-digit",month:"2-digit",hour:"2-digit",minute:"2-digit"})}</div>
                      <div className="flex items-center gap-1 text-[10px]">
                        {l.email_sent && <span className="text-emerald-700" title="E-mail enviado"><Mail className="inline w-3 h-3"/></span>}
                        {l.sms_sent && <span className="text-violet-700" title="SMS enviado"><MessageSquare className="inline w-3 h-3"/></span>}
                      </div>
                    </div>
                  </div>
                  {waLink && (
                    <a href={waLink} target="_blank" rel="noreferrer"
                       className="mt-2 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-[11px] transition"
                       data-testid={`fallback-wa-reply-${l.id}`}>
                      <MessageCircle className="w-3.5 h-3.5"/> Responder no WhatsApp <ExternalLink className="w-3 h-3 opacity-70"/>
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
